import { FetchClient, storeClient } from '@/shared/api/client';
import { SeoResponse } from '@/shared/api/types';
import { sortByPosition } from '@/shared/utils/sort-by-position';
import { sortProductsInProductComplementGroup } from '@/shared/utils/sort-products-in-product-complement-group';

import {
  AncillaryProducts,
  AncillaryProductsResponse,
  CrossRewriteProductResponse,
  IAncillaryProduct,
  IProductComplementGroup,
  ProductDescription,
  ProductRedirectResponse,
  ProductResponse,
  ProductReviewsResponse,
  SeriesProduct,
  VariantOption,
  VariantResponse,
} from './types';

export const fetchProductRedirect = async (
  categoryId: string,
  fetch?: FetchClient
): Promise<ProductRedirectResponse> => {
  try {
    return await storeClient<ProductRedirectResponse>(
      `/product-redirect/get/${categoryId}`,
      {},
      fetch
    ).catch((error) => {
      return {
        redirectTo: null,
      };
    });
  } catch (error) {
    return {
      redirectTo: null,
    };
  }
};

export const fetchProduct = async (
  productSlug: string,
  fetch?: FetchClient
): Promise<ProductResponse | null> => {
  try {
    const result = await storeClient<ProductResponse>(
      `/product/show/${productSlug}`,
      {},
      fetch
    );

    return result;
  } catch {
    return null;
  }
};

export const fetchVariantGroup = async (
  productId: string,
  fetch?: FetchClient
): Promise<VariantOption[]> => {
  try {
    const result = await storeClient<VariantResponse>(
      `/product-variant/show/${productId}`,
      {},
      fetch
    );

    return result.options;
  } catch {
    return [];
  }
};

export const fetchProductSeo = async (
  slug: string,
  fetch?: FetchClient
): Promise<SeoResponse> => {
  try {
    const result = await storeClient<SeoResponse>(
      `/product/seo/${slug}`,
      {},
      fetch
    );

    return result;
  } catch (e) {
    return {
      locale: '',
      canonical: '',
      robots: '',
      meta: {
        description: '',
        title: '',
        canonical: null,
      },
    };
  }
};

export const fetchOtherSeries = async (
  productSlug: string,
  fetch?: FetchClient
): Promise<SeriesProduct[]> => {
  try {
    const result = await storeClient<SeriesProduct[]>(
      `/product/other-series/${productSlug}`,
      {},
      fetch
    );

    return result;
  } catch {
    return [];
  }
};

export const fetchOtherFromCategory = async (
  productSlug: string,
  fetch?: FetchClient
): Promise<SeriesProduct[]> => {
  try {
    const result = await storeClient<SeriesProduct[]>(
      `/product/other-from-category/${productSlug}`,
      {},
      fetch
    );
    return result;
  } catch {
    return [];
  }
};

export const fetchAncillaryProducts = async (
  productId: string,
  fetch?: FetchClient
): Promise<AncillaryProducts> => {
  try {
    const result = await storeClient<AncillaryProductsResponse>(
      `/product-complement/show/${productId}`,
      {},
      fetch
    );

    return {
      products: sortByPosition<IAncillaryProduct>(result.products),
      groups: sortByPosition<IProductComplementGroup>(result.groups).map(
        (group) => ({
          ...group,
          products: sortByPosition(group.products),
        })
      ),
    };
  } catch (e) {
    console.error(e);
    return {
      groups: [],
      products: [],
    };
  }
};

export const fetchCrossRewriteProduct = async (
  slug: string,
  fetch?: FetchClient
): Promise<CrossRewriteProductResponse> => {
  try {
    const result = await storeClient<CrossRewriteProductResponse>(
      `/product/cross-rewrite/${slug}`,
      {},
      fetch
    );

    if (!Array.isArray(result)) {
      return [];
    }

    return result;
  } catch (e) {
    return [];
  }
};

export const fetchProductReviews = async (
  productId: string,
  fetch?: FetchClient
): Promise<ProductReviewsResponse> => {
  try {
    const result = await storeClient<ProductReviewsResponse>(
      `/product-review/show/${productId}`,
      {},
      fetch
    );

    return result;
  } catch {
    return [];
  }
};

export const fetchProductDescription = async (
  productId: string,
  fetch?: FetchClient
): Promise<ProductDescription | null> => {
  try {
    const result = await storeClient<ProductDescription | null>(
      `/product-description/show/${productId}`,
      {},
      fetch
    );

    return result;
  } catch {
    return null;
  }
};

export const createAvailabilityNotification = async (
  productId: string,
  emailAddress: string,
  fetch?: FetchClient
): Promise<boolean> => {
  try {
    const result = await storeClient<unknown>(
      `/product-availability-notification/add`,
      {
        method: 'POST',
        body: {
          productId: productId,
          emailAddress: emailAddress,
        },
      },
      fetch
    );

    return true;
  } catch (e) {
    return false;
  }
};
